<script lang="ts" setup>
import { useField } from 'vee-validate'
import type { DropdownOption } from '~/types/app'

const slots = useSlots()

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  placeholder: {
    type: String,
    required: false,
    default: '',
  },
  options: {
    type: Array as () => DropdownOption[],
    required: true,
    default: () => [],
  },
  clearable: {
    type: Boolean,
    required: false,
    default: true,
  },
  contentClass: {
    type: String,
    required: false,
    default: '',
  },
  offset: {
    type: [String, Number],
    required: false,
    default: 10,
  },
  scrollBehavior: {
    type: String as PropType<'none' | 'block' | 'close'>,
    required: false,
    default: 'none',
  },
})

const emits = defineEmits(['onModelChange'])
const { value, errorMessage } = useField(() => props.name)

const onClear = () => {
  value.value = ''
}
</script>

<template>
  <ClientOnly>
    <div class="form-dropdown">
      <label v-if="label" class="form-dropdown__label" :for="name">{{
        label
      }}</label>
      <VSelect
        v-model="value"
        :clearable="clearable"
        :label="placeholder"
        item-value="key"
        :menu-props="{
          contentClass: `v-select__content ${contentClass}`,
          offset: offset,
          scrollStrategy: scrollBehavior,
        }"
        hide-details
        :error="Boolean(errorMessage)"
        :placeholder="placeholder"
        :items="options"
        single-line
        variant="outlined"
        density="comfortable"
        @click:clear="onClear"
        @update:model-value="emits('onModelChange', $event)"
      >
        <template v-if="slots['prepend-item']" #prepend-item>
          <slot name="prepend-item" />
        </template>
        <template v-if="slots.item" #item="{ item, index, props: itemProps }">
          <slot name="item" :item="item" :index="index" :props="itemProps" />
        </template>
        <template v-if="slots.selection" #selection="{ item, index }">
          <slot name="selection" :item="item" :index="index" />
        </template>
        <template v-if="slots['no-data']" #append-item>
          <slot name="no-data" />
        </template>
        <template #clear>
          <i class="icon-delete" @click="onClear" />
        </template>
      </VSelect>
      <span v-if="errorMessage?.trim()" class="form-dropdown__error-message">{{
        errorMessage
      }}</span>
    </div>
  </ClientOnly>
</template>
